import React, { useEffect, useState, useRef } from "react";
import { Save, Settings as SettingsIcon, Activity, Palette, Image as ImageIcon, Upload, Loader2, Contact2 } from "lucide-react";
import { toast } from "sonner";
import api, { notifyError, formatApiError, imageUrl } from "@/lib/api";
import PlatformLayout from "@/components/PlatformLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { usePlatformBranding } from "@/context/PlatformBrandingContext";

const EMPTY = {
  platform_name: "", platform_tagline: "", platform_description: "", platform_version: "",
  platform_logo: "", platform_favicon: "",
  platform_primary_color: "#E85D04", platform_secondary_color: "#4A2E1F",
  support_email: "", support_whatsapp: "", website_url: "", copyright_text: "",
  platform_status: "operational",
};

function UploadButton({ label, value, onChange, testId }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = () => inputRef.current?.click();
  const handle = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/platform/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Simpan `path` relatif ({user_id}/{uuid}.ext) supaya `imageUrl()` bisa
      // menyusun URL yang benar via `${API}/files/{path}` (endpoint serve_file
      // yang menghormati ACL). `r.data.url` mengandung prefix /uploads/ untuk
      // Nginx static — kurang portable dan menyebabkan double-slash saat
      // dibungkus imageUrl().
      const path = r.data?.path || r.data?.url || r.data?.filename;
      if (path) {
        onChange(path);
        toast.success(`${label} berhasil di-upload`);
      } else {
        toast.error("Upload response tidak berisi path");
      }
    } catch (err) { toast.error(formatApiError(err) || "Gagal upload"); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  return (
    <div className="flex items-center gap-3 flex-wrap">
      {value ? (
        <img src={imageUrl(value)} alt={label} className="h-10 w-10 rounded object-cover bg-white/10" onError={(e) => { e.target.style.opacity = 0.3; }} />
      ) : (
        <div className="h-10 w-10 rounded bg-white/10 grid place-items-center text-white/30"><ImageIcon size={16} /></div>
      )}
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={`Path ${label}`} className="flex-1 min-w-[200px] bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-white placeholder:text-white/30 font-mono text-xs" data-testid={testId} />
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handle} />
      <Button type="button" variant="outline" size="sm" onClick={pick} disabled={busy} className="border-white/20 text-white hover:bg-white/10" data-testid={`${testId}-upload`}>
        {busy ? <Loader2 size={12} className="animate-spin mr-1" /> : <Upload size={12} className="mr-1" />}
        {busy ? "Upload..." : "Upload"}
      </Button>
    </div>
  );
}

export default function PlatformSettingsPage() {
  const { reload: reloadBranding } = usePlatformBranding();
  const [s, setS] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/platform/settings")
      .then((r) => setS({ ...EMPTY, ...r.data }))
      .catch(notifyError)
      .finally(() => setLoading(false));
  }, []);

  const upd = (k, v) => setS((x) => ({ ...x, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/platform/settings", s);
      setS({ ...EMPTY, ...r.data.settings });
      await reloadBranding();
      toast.success("Platform branding tersimpan. Halaman akan pakai identitas baru.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <PlatformLayout title="Platform Branding"><div className="text-white/60">Memuat...</div></PlatformLayout>;

  return (
    <PlatformLayout title="Platform Branding">
      <div className="max-w-3xl space-y-4" data-testid="platform-settings-page">
        <p className="text-sm text-white/50 -mt-4 mb-2">Identitas Platform SaaS (BUKAN branding toko). Setting ini muncul di landing page, halaman login platform, dan sidebar dashboard.</p>

        {/* Identity */}
        <Card className="bg-white/5 border-white/10 p-5 rounded-2xl">
          <h3 className="text-sm font-display font-bold text-white mb-4 flex items-center gap-2"><SettingsIcon size={14} /> Identitas</h3>
          <div className="grid gap-4">
            <div>
              <Label className="text-white/60 text-xs">Platform Name</Label>
              <Input value={s.platform_name} onChange={(e) => upd("platform_name", e.target.value)} placeholder="Dynicty" className="bg-white/10 border-white/10 text-white placeholder:text-white/30" data-testid="ps-name" />
            </div>
            <div>
              <Label className="text-white/60 text-xs">Tagline</Label>
              <Input value={s.platform_tagline} onChange={(e) => upd("platform_tagline", e.target.value)} placeholder="White Label E-Commerce Platform" className="bg-white/10 border-white/10 text-white placeholder:text-white/30" data-testid="ps-tagline" />
            </div>
            <div>
              <Label className="text-white/60 text-xs">Description</Label>
              <Textarea value={s.platform_description} onChange={(e) => upd("platform_description", e.target.value)} rows={3} placeholder="Deskripsi singkat platform..." className="bg-white/10 border-white/10 text-white placeholder:text-white/30" data-testid="ps-description" />
            </div>
            <div>
              <Label className="text-white/60 text-xs">Version</Label>
              <Input value={s.platform_version} onChange={(e) => upd("platform_version", e.target.value)} placeholder="1.0.0" className="bg-white/10 border-white/10 text-white placeholder:text-white/30" data-testid="ps-version" />
            </div>
          </div>
        </Card>

        {/* Visual */}
        <Card className="bg-white/5 border-white/10 p-5 rounded-2xl">
          <h3 className="text-sm font-display font-bold text-white mb-4 flex items-center gap-2"><Palette size={14} /> Visual</h3>
          <div className="grid gap-4">
            <div>
              <Label className="text-white/60 text-xs">Logo Platform</Label>
              <UploadButton label="Logo" value={s.platform_logo} onChange={(v) => upd("platform_logo", v)} testId="ps-logo" />
            </div>
            <div>
              <Label className="text-white/60 text-xs">Favicon</Label>
              <UploadButton label="Favicon" value={s.platform_favicon} onChange={(v) => upd("platform_favicon", v)} testId="ps-favicon" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white/60 text-xs">Primary Color</Label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={s.platform_primary_color || "#E85D04"} onChange={(e) => upd("platform_primary_color", e.target.value)} className="h-10 w-14 rounded bg-transparent border border-white/10 cursor-pointer" data-testid="ps-primary-color" />
                  <Input value={s.platform_primary_color} onChange={(e) => upd("platform_primary_color", e.target.value)} className="bg-white/10 border-white/10 text-white font-mono text-xs" />
                </div>
              </div>
              <div>
                <Label className="text-white/60 text-xs">Secondary Color</Label>
                <div className="flex gap-2 items-center">
                  <input type="color" value={s.platform_secondary_color || "#4A2E1F"} onChange={(e) => upd("platform_secondary_color", e.target.value)} className="h-10 w-14 rounded bg-transparent border border-white/10 cursor-pointer" data-testid="ps-secondary-color" />
                  <Input value={s.platform_secondary_color} onChange={(e) => upd("platform_secondary_color", e.target.value)} className="bg-white/10 border-white/10 text-white font-mono text-xs" />
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Contact & Meta */}
        <Card className="bg-white/5 border-white/10 p-5 rounded-2xl">
          <h3 className="text-sm font-display font-bold text-white mb-4 flex items-center gap-2"><Contact2 size={14} /> Kontak & Meta</h3>
          <div className="grid gap-4">
            <div>
              <Label className="text-white/60 text-xs">Support Email</Label>
              <Input type="email" value={s.support_email} onChange={(e) => upd("support_email", e.target.value)} placeholder="support@platformanda.com" className="bg-white/10 border-white/10 text-white placeholder:text-white/30" data-testid="ps-support-email" />
            </div>
            <div>
              <Label className="text-white/60 text-xs">Support WhatsApp</Label>
              <Input value={s.support_whatsapp} onChange={(e) => upd("support_whatsapp", e.target.value)} placeholder="6281234567890" className="bg-white/10 border-white/10 text-white placeholder:text-white/30" data-testid="ps-support-wa" />
            </div>
            <div>
              <Label className="text-white/60 text-xs">Website URL</Label>
              <Input value={s.website_url} onChange={(e) => upd("website_url", e.target.value)} placeholder="https://platformanda.com" className="bg-white/10 border-white/10 text-white placeholder:text-white/30" data-testid="ps-website" />
            </div>
            <div>
              <Label className="text-white/60 text-xs">Copyright</Label>
              <Input value={s.copyright_text} onChange={(e) => upd("copyright_text", e.target.value)} placeholder="© Dynicty" className="bg-white/10 border-white/10 text-white placeholder:text-white/30" data-testid="ps-copyright" />
            </div>
          </div>
        </Card>

        {/* Status */}
        <Card className="bg-white/5 border-white/10 p-5 rounded-2xl">
          <h3 className="text-sm font-display font-bold text-white mb-4 flex items-center gap-2"><Activity size={14} /> Status Platform</h3>
          <div className="grid grid-cols-3 gap-2">
            {["operational", "degraded", "maintenance"].map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => upd("platform_status", v)}
                className={`p-3 rounded-xl border transition-colors text-xs font-bold uppercase tracking-wider ${
                  s.platform_status === v ? "border-white/50 text-white" : "border-white/10 text-white/50 hover:text-white/80"
                }`}
                style={s.platform_status === v ? { backgroundColor: "color-mix(in srgb, var(--platform-primary, var(--brand-primary)) 25%, transparent)" } : undefined}
                data-testid={`ps-status-${v}`}
              >{v}</button>
            ))}
          </div>
        </Card>

        <div className="sticky bottom-4">
          <Button onClick={save} disabled={saving} className="brand-btn-primary rounded-xl h-12 px-6 font-bold shadow-lg" data-testid="ps-save-btn">
            <Save size={16} className="mr-2" /> {saving ? "Menyimpan..." : "Simpan Platform Branding"}
          </Button>
        </div>
      </div>
    </PlatformLayout>
  );
}
